import { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceSelect,
} from '@/components/ui/workspace';
import {
  useCreateFindingMutation,
  useUpdateFindingMutation,
  type UpdateFindingBody,
} from '@/lib/api/health-findings';
import { useHealthDomainsQuery } from '@/lib/api/health-domains';
import { useFindingOptionsQuery } from '@/lib/api/health-readiness-report';
import { useOverrideTaxonomyQuery } from '@/lib/api/overrides';
import { humanize } from './pane-header';

/**
 * The one finding form, used three ways: New finding on the Findings tab,
 * Edit on a register row there (T-0244), and Confirm on a proposed finding
 * in an interview note (the AI proposal is the starting point; the API
 * flips it to accepted in the same write and links the note's document as
 * evidence).
 *
 * Types, severities, statuses and source kinds come from the API
 * (`…/health-readiness/finding-options`); the domain is picked from this
 * engagement's active domains, never typed. The finding's type is kept in
 * the register's `category`. An edit sends only what changed through
 * `PATCH …/health/findings/:id`; a severity change carries the reason the
 * API requires (a tag from the override taxonomy and a sentence).
 */

export interface FindingFormInitial {
  title?: string;
  type?: string;
  severity?: string;
  domainId?: string;
  description?: string;
  status?: string;
}

export interface FindingFormProps {
  engagementId: string;
  heading: string;
  submitLabel: string;
  initial?: FindingFormInitial;
  /** Set when confirming an AI proposal from an interview note. */
  fromProposal?: { proposalId: string; documentId: string | null };
  /** Set when editing a finding on the register. */
  findingId?: string;
  onCancel: () => void;
  onSaved: () => void;
}

/** The API's floor for an override rationale. */
const MIN_REASON_CHARS = 10;

const pick = (wanted: string | undefined, allowed: readonly string[], fallback: string) => {
  if (wanted) {
    const hit = allowed.find((v) => v.toLowerCase() === wanted.toLowerCase());
    if (hit) return hit;
  }
  return allowed.includes(fallback) ? fallback : (allowed[0] ?? '');
};

export function FindingForm(props: FindingFormProps) {
  const options = useFindingOptionsQuery(props.engagementId);
  const domains = useHealthDomainsQuery(props.engagementId);

  if (options.isLoading || domains.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading the finding form…
      </div>
    );
  }
  if (options.error) return <ErrorMessage error={options.error} />;
  if (!options.data) return null;
  return (
    <FindingFormFields
      {...props}
      types={options.data.finding_types}
      severities={options.data.severities}
      statuses={options.data.statuses}
      sourceKinds={options.data.source_kinds}
      domains={domains.data?.domains ?? []}
    />
  );
}

function FindingFormFields({
  engagementId,
  heading,
  submitLabel,
  initial = {},
  fromProposal,
  findingId,
  onCancel,
  onSaved,
  types,
  severities,
  statuses,
  sourceKinds,
  domains,
}: FindingFormProps & {
  types: readonly string[];
  severities: readonly string[];
  statuses: readonly string[];
  sourceKinds: readonly string[];
  domains: ReadonlyArray<{ id: string; name: string }>;
}) {
  const editing = Boolean(findingId);
  const create = useCreateFindingMutation(engagementId);
  const update = useUpdateFindingMutation(engagementId);
  const save = editing ? update : create;

  // An edit keeps a value the lists no longer offer (a retired domain, a
  // type taken out of the firm's list) rather than silently changing it.
  const typeOptions =
    editing && initial.type && !types.includes(initial.type) ? [initial.type, ...types] : types;
  const domainOptions =
    editing && initial.domainId && !domains.some((d) => d.id === initial.domainId)
      ? [...domains, { id: initial.domainId, name: 'Current domain (retired)' }]
      : domains;

  const [title, setTitle] = useState(initial.title ?? '');
  const [type, setType] = useState(() =>
    editing ? (initial.type ?? '') : pick(initial.type, types, ''),
  );
  const [severity, setSeverity] = useState(() => pick(initial.severity, severities, 'medium'));
  const [status, setStatus] = useState(initial.status ?? 'open');
  const [sourceKind, setSourceKind] = useState(() =>
    pick(fromProposal ? 'interview' : undefined, sourceKinds, 'manual'),
  );
  const [domainId, setDomainId] = useState(
    initial.domainId && domainOptions.some((d) => d.id === initial.domainId)
      ? initial.domainId
      : '',
  );
  const [description, setDescription] = useState(initial.description ?? '');
  const [reasonTag, setReasonTag] = useState('other');
  const [reason, setReason] = useState('');

  const severityChanged = editing && severity !== (initial.severity ?? '');
  const reasonOk =
    !severityChanged || (reasonTag.length > 0 && reason.trim().length >= MIN_REASON_CHARS);
  const canSubmit =
    title.trim().length > 0 &&
    type.length > 0 &&
    severity.length > 0 &&
    reasonOk &&
    !save.isPending;

  function submit() {
    if (!canSubmit) return;
    if (findingId) {
      update.mutate({ findingId, body: editBody() }, { onSuccess: () => onSaved() });
      return;
    }
    create.mutate(
      {
        title: title.trim(),
        category: type,
        severity,
        source_kind: sourceKind,
        description: description.trim() || null,
        health_domain_id: domainId || null,
        ...(fromProposal
          ? {
              from_proposal_id: fromProposal.proposalId,
              source_document_id: fromProposal.documentId,
            }
          : {}),
      },
      { onSuccess: () => onSaved() },
    );
  }

  /** Only what changed; the severity's reason rides with it. */
  function editBody(): UpdateFindingBody {
    const body: UpdateFindingBody = {};
    if (title.trim() !== (initial.title ?? '').trim()) body.title = title.trim();
    if (type !== (initial.type ?? '')) body.category = type;
    if (domainId !== (initial.domainId ?? '')) body.health_domain_id = domainId || null;
    if (status !== (initial.status ?? 'open')) body.status = status;
    if (description.trim() !== (initial.description ?? '').trim()) {
      body.description = description.trim() || null;
    }
    if (severityChanged) {
      body.severity = severity;
      body.override = { tag: reasonTag, rationale: reason.trim() };
    }
    return body;
  }

  return (
    <section
      aria-label={heading}
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
    >
      <h3 className="t-h3">{heading}</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <WorkspaceField label="Title" className="sm:col-span-2 lg:col-span-4">
          {(id) => (
            <WorkspaceInput
              id={id}
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. UAT scripts cover 62% of in-scope scenarios"
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Type">
          {(id) => (
            <WorkspaceSelect id={id} value={type} onChange={(e) => setType(e.target.value)}>
              {typeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
        <WorkspaceField label="Severity">
          {(id) => (
            <WorkspaceSelect id={id} value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {severities.map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
        <WorkspaceField label="Domain">
          {(id) => (
            <WorkspaceSelect id={id} value={domainId} onChange={(e) => setDomainId(e.target.value)}>
              <option value="">No domain</option>
              {domainOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
        {editing ? (
          <WorkspaceField label="Status">
            {(id) => (
              <WorkspaceSelect id={id} value={status} onChange={(e) => setStatus(e.target.value)}>
                {(statuses.includes(status) ? statuses : [status, ...statuses]).map((s) => (
                  <option key={s} value={s}>
                    {humanize(s)}
                  </option>
                ))}
              </WorkspaceSelect>
            )}
          </WorkspaceField>
        ) : fromProposal ? null : (
          <WorkspaceField label="Source">
            {(id) => (
              <WorkspaceSelect
                id={id}
                value={sourceKind}
                onChange={(e) => setSourceKind(e.target.value)}
              >
                {sourceKinds.map((s) => (
                  <option key={s} value={s}>
                    {humanize(s)}
                  </option>
                ))}
              </WorkspaceSelect>
            )}
          </WorkspaceField>
        )}
        {severityChanged ? <SeverityReason tag={reasonTag} onTag={setReasonTag} /> : null}
        {severityChanged ? (
          <WorkspaceField
            label="Why the severity changes"
            className="sm:col-span-2 lg:col-span-3"
            help={`At least ${MIN_REASON_CHARS} characters; kept with the change.`}
          >
            {(id) => (
              <WorkspaceInput id={id} value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </WorkspaceField>
        ) : null}
        <WorkspaceField label="Description" className="sm:col-span-2 lg:col-span-4">
          {(id) => (
            <WorkspaceInput
              id={id}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </WorkspaceField>
      </div>
      {save.error ? <ErrorMessage error={save.error} className="mt-2" /> : null}
      <div className="mt-3 flex justify-end gap-2">
        <WorkspaceButton variant="tertiary" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
        <WorkspaceButton variant="primary" disabled={!canSubmit} onClick={submit}>
          {save.isPending ? <Spinner /> : null}
          {submitLabel}
        </WorkspaceButton>
      </div>
    </section>
  );
}

/** The override taxonomy's reasons, which the API checks the tag against. */
function SeverityReason({ tag, onTag }: { tag: string; onTag: (tag: string) => void }) {
  const taxonomy = useOverrideTaxonomyQuery();
  const tags = taxonomy.data?.tags;
  return (
    <WorkspaceField label="Reason">
      {(id) => (
        <WorkspaceSelect
          id={id}
          value={tag}
          disabled={!tags}
          onChange={(e) => onTag(e.target.value)}
        >
          {tags ? (
            tags.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))
          ) : (
            <option value={tag}>
              {taxonomy.isError ? 'Reasons could not be loaded' : 'Loading reasons…'}
            </option>
          )}
        </WorkspaceSelect>
      )}
    </WorkspaceField>
  );
}
